import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function cleanEnvValue(value) {
  if (!value) return '';
  let v = String(value).trim();
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    v = v.slice(1, -1).trim();
  }
  return v;
}

const firebaseConfig = {
  apiKey: cleanEnvValue(process.env.REACT_APP_FIREBASE_API_KEY),
  authDomain: cleanEnvValue(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN),
  projectId: cleanEnvValue(process.env.REACT_APP_FIREBASE_PROJECT_ID),
  messagingSenderId: cleanEnvValue(process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID),
  appId: cleanEnvValue(process.env.REACT_APP_FIREBASE_APP_ID),
};

let messagingInstance = null;

async function getMessagingInstance() {
  if (messagingInstance) return messagingInstance;
  if (!firebaseConfig.apiKey) return null;
  const supported = await isSupported().catch(() => false);
  if (!supported) return null;
  const existing = getApps().find((app) => app.name === 'machineos-push');
  const app = existing || initializeApp(firebaseConfig, 'machineos-push');
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

async function saveFcmToken(authToken, token) {
  await fetch(`${API_BASE_URL}/api/notifications/fcm-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      token,
      deviceInfo: navigator.userAgent,
    }),
  });
}

export async function deleteCurrentFcmToken(authToken) {
  try {
    if (!authToken || !firebaseConfig.apiKey) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    const messaging = await getMessagingInstance();
    if (!messaging) return;

    const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')
      || await navigator.serviceWorker.getRegistration();
    if (!registration) return;

    const token = await getToken(messaging, {
      vapidKey: cleanEnvValue(process.env.REACT_APP_FIREBASE_VAPID_KEY),
      serviceWorkerRegistration: registration,
    });
    if (!token) return;

    await fetch(`${API_BASE_URL}/api/notifications/fcm-token`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ token }),
    });
  } catch (_err) {
    // silent fail — disable must continue even if token delete fails
  }
}

export async function requestPushPermission(authToken) {
  try {
    if (!authToken || !firebaseConfig.apiKey) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    const messaging = await getMessagingInstance();
    if (!messaging) return;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const token = await getToken(messaging, {
      vapidKey: cleanEnvValue(process.env.REACT_APP_FIREBASE_VAPID_KEY),
      serviceWorkerRegistration: registration,
    });

    if (!token) return;
    await saveFcmToken(authToken, token);
  } catch (err) {
    console.warn('Push permission failed (non-critical):', err);
  }
}

export function onForegroundMessage(callback) {
  let unsubscribe = () => {};
  getMessagingInstance().then((messaging) => {
    if (!messaging) return;
    unsubscribe = onMessage(messaging, callback);
  }).catch(() => {});
  return () => unsubscribe();
}
